import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import treatments from '../data/treatments';

const Treatments = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const handleToggle = (index) => {
    setExpandedIndex(prev => (prev === index ? null : index));
  };

  return (
    <section className="py-12 md:py-20 bg-teal-50 relative overflow-hidden">
      <motion.div 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 0.1 }}
        transition={{ duration: 1 }}
        className="absolute inset-0 overflow-hidden"
      >
        <motion.div 
          animate={{ x: [0, 20, 0], y: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className="absolute top-1/4 left-10 w-32 h-32 rounded-full bg-teal-200 blur-xl opacity-50"
        />
      </motion.div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-teal-900 mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Treatments 
          </h2>
          <div className="w-20 h-1 bg-teal-600 mx-auto" />
        </motion.div>

        <div className="hidden md:block">
          <StyledDesktopWrapper>
            <div className="card">
              {treatments.map((treatment, index) => (
                <motion.p 
                  key={index}
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true, amount: 0.5 }}
                >
                  <span>{treatment.title}</span>
                  <div className="details">
                    <img className="description rounded-lg" src={treatment.image} alt={treatment.title} />
                    <Link to={`/treatments/${treatment.slug}`} className="view-more -mb-1">
                      View More
                    </Link>
                  </div>
                </motion.p>
              ))}
            </div>
          </StyledDesktopWrapper>
        </div>

        <div className="md:hidden">
          <StyledMobileWrapper>
            <div className="card">
              {treatments.map((treatment, index) => (
                <motion.p 
                  key={index}
                  onClick={() => handleToggle(index)}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  viewport={{ once: true }}
                >
                  <span>{treatment.title}</span>
                  {expandedIndex === index && (
                    <div className="details flex justify-center items-center flex-col">
                      <img 
                        src={treatment.image} 
                        alt={treatment.title} 
                      />
                      <Link 
                        to={`/treatments/${treatment.slug}`} 
                        className="view-more"
                        onClick={(e) => e.stopPropagation()}
                      >
                        View More
                      </Link>
                    </div>
                  )}
                </motion.p>
              ))}
            </div>
          </StyledMobileWrapper>
        </div>
      </div>
    </section>
  );
};

const StyledDesktopWrapper = styled.div`
  .card {
    width: 100%;
    height: 320px;
    border-radius: 12px;
    background: #f8e9dd;
    display: flex;
    gap: 10px;
    padding: 1em;
    box-shadow: 0 8px 24px rgba(15, 118, 110, 0.1);
  }
  .card p {
    min-height: 150px;
    padding: 1rem 0.5rem;
    flex: 1;
    overflow: hidden;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    background: #fdf6f3;
    border: 1px solid #e3a97d;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
  }
  .card p:hover {
    flex: 2.7;
    box-shadow: 0 10px 20px rgba(227, 169, 125, 0.2);
  }
  .card p span {
    min-width: 14em;
    padding: 0.5em;
    text-align: center;
    transform: rotate(-90deg);
    text-transform: uppercase;
    color: #c77845;
    letter-spacing: 0.1em;
    font-weight: 600;
    font-family: 'Montserrat', sans-serif;
    position: absolute;
  }
  .card p:hover span {
    transform: rotate(0);
    position: relative;
    margin-bottom: 1rem;
    color: #874d32;
  }
  .card .details {
    opacity: 0;
    transition: opacity 0.3s ease 0.2s;
    text-align: center;
    width: 100%;
    padding: 0 1rem;
  }
  .card p:hover .details {
    opacity: 1;
  }
  .card .details img {
    object-fit: cover;
    margin: 0 auto;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    width: 180px;
    height: 180px;
  }
  @media (min-width: 768px) {
    .card .details img {
      width: 180px;
      height: 180px;
    }
  }
  @media (min-width: 1024px) {
    .card .details img {
      width: 1800px;
      height: 180px;
    }
  }
  .card .view-more {
    display: inline-block;
    padding: 0.6rem 1.2rem;
    background-color: #e3a97d;
    color: white;
    border-radius: 6px;
    text-decoration: none;
    font-size: 0.8rem;
    transition: all 0.3s ease;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    border: 1px solid #d99161;
  }
  .card .view-more:hover {
    background-color: #c77845;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(199, 120, 69, 0.3);
  }
`;

const StyledMobileWrapper = styled.div`
  .card {
    width: 100%;
    border-radius: 12px;
    background: linear-gradient(145deg, #f8e9dd, #f1d3bb);
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 0.8em;
    box-shadow: 0 8px 24px rgba(15, 118, 110, 0.1);
  }
  .card p {
    flex: 1;
    min-height: 60px;
    overflow: hidden;
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
    background: linear-gradient(145deg, #fdf6f3, #f8e9dd);
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
    border: 1px solid #e3a97d;
  }
  .card p:hover {
    min-height: 120px;
    box-shadow: 0 6px 12px rgba(227, 169, 125, 0.2);
  }
  .card p span {
    padding: 0.5em;
    text-align: center;
    transform: rotate(0);
    text-transform: uppercase;
    color: #c77845;
    font-weight: 600;
    letter-spacing: 0.1em;
    position: relative;
    z-index: 1;
    font-family: 'Montserrat', sans-serif;
  }
  .card .details {
    opacity: 1;
    transition: opacity 0.3s ease 0.2s;
    text-align: center;
    width: 100%;
  }
 .card .details img {
  object-fit: cover;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 200px;
  height: 200px;
}
@media (min-width: 400px) {
  .card .details img {
    width: 80px;
    height: 80px;
  }
}
@media (min-width: 640px) {
  .card .details img {
    width: 100px;
    height: 100px;
  }
}

  .card .view-more {
    display: inline-block;
    padding: 0.5rem 1rem;
    background-color: #e3a97d;
    color: white;
    border-radius: 6px;
    text-decoration: none;
    font-size: 0.8rem;
    transition: all 0.3s ease;
    font-family: 'Montserrat', sans-serif;
    font-weight: 500;
    margin-top: 0.5rem;
    border: 1px solid #d99161;
  }
  .card .view-more:hover {
    background-color: #c77845;
    transform: translateY(-2px);
  }
  .card p::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(227, 169, 125, 0.1);
    z-index: 0;
    transition: opacity 0.5s;
    pointer-events: none;
    opacity: 0;
  }
  .card p:hover::before {
    opacity: 1;
  }
`;

export default Treatments;
